<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  checkForAppUpdate,
  openApkDownload,
  type AppUpdateInfo,
} from '@/services/appUpdate'

const { t, locale } = useI18n()

const update = ref<AppUpdateInfo | null>(null)
const visible = ref(false)

onMounted(async () => {
  const info = await checkForAppUpdate()
  if (!info) return
  update.value = info
  visible.value = true
  if (info.forceUpdate) {
    document.addEventListener('keydown', onKeydown, true)
  }
})

onUnmounted(() => {
  document.removeEventListener('keydown', onKeydown, true)
})

function onKeydown(e: KeyboardEvent) {
  if (!update.value?.forceUpdate || !visible.value) return
  // Hard gate: block Escape / Back-style dismiss
  if (e.key === 'Escape') {
    e.preventDefault()
    e.stopPropagation()
  }
}

function dismiss() {
  if (update.value?.forceUpdate) return
  visible.value = false
}

function onBackdropClick() {
  if (update.value?.forceUpdate) return
  dismiss()
}

function confirmUpdate() {
  if (!update.value) return
  openApkDownload(update.value.installUrl)
}
</script>

<template>
  <div
    v-if="visible && update"
    class="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 p-4"
    role="dialog"
    aria-modal="true"
    :aria-label="t('app_update.title')"
    @click.self="onBackdropClick"
  >
    <div
      class="w-full max-w-sm rounded-xl bg-surface-light dark:bg-surface-dark p-5 shadow-xl border border-primary/20"
      :dir="locale === 'ar' ? 'rtl' : 'ltr'"
    >
      <div class="flex items-center gap-3 mb-3">
        <div class="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-white">
          <span class="material-symbols-outlined">system_update</span>
        </div>
        <div>
          <h2 class="font-display text-lg font-bold text-text-primary-light dark:text-text-primary-dark">
            {{ t('app_update.title') }}
          </h2>
          <p class="text-xs text-text-secondary-light dark:text-text-secondary-dark">
            {{ t('app_update.version_line', { current: update.currentVersion, latest: update.latestVersion }) }}
          </p>
        </div>
      </div>

      <p class="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-3">
        {{ t('app_update.message') }}
      </p>

      <p
        v-if="update.releaseNotes"
        class="mb-4 max-h-28 overflow-y-auto rounded-lg bg-background-light dark:bg-background-dark p-3 text-xs text-text-secondary-light dark:text-text-secondary-dark whitespace-pre-wrap"
      >
        {{ update.releaseNotes }}
      </p>

      <div class="flex flex-col gap-2">
        <button
          type="button"
          class="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary/90"
          @click="confirmUpdate"
        >
          {{ update.usesStore ? t('app_update.update_store') : t('app_update.update') }}
        </button>
        <button
          v-if="!update.forceUpdate"
          type="button"
          class="w-full py-2 text-sm text-text-secondary-light dark:text-text-secondary-dark hover:underline"
          @click="dismiss"
        >
          {{ t('app_update.later') }}
        </button>
      </div>
    </div>
  </div>
</template>
